/**
 * SVG 渲染层 —— 把分区、器材与连接画出来。
 * 对应技术设计 §12.1（器材用 SVG，精确锚点、局部高亮、缩放清晰）与
 * §5.5（「更新 SVG path，不重排 DOM」）。
 *
 * 【为什么必须增量更新】
 * P0 版本每次 render 都 `replaceChildren` 整棵树。拖拽是每帧调用的：
 * 全量重建会丢掉 `setPointerCapture` 绑定的元素、丢掉 CSS 过渡，在手机上明显掉帧。
 * 所以这里分两条路径：
 *   render()  —— 建/重建全量 DOM（加载、切模式、reset 时调用）
 *   sync()    —— 只更新 transform 与变化的图元（拖拽每帧调用，rAF 合帧）
 *
 * 学习模式显示连接点（§4.2 `:257`）；练习模式靠近才显示，由 `highlight()` 驱动。
 */

import type { LabMode } from "../data/schemas.js";
import type { LabObject } from "../eom/lab-object.js";
import { toWorld } from "../eom/lab-object.js";
import type { Shape } from "../eom/equipment-registry.js";
import { getTemplate, resolveReactive } from "../eom/equipment-registry.js";
import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { listRelations } from "../eom/assembly-graph.js";
import { resolveTubeEnds } from "../interaction/constraint-solver.js";
import type { Scene } from "../scene/scene-manager.js";

const SVG_NS = "http://www.w3.org/2000/svg";

function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) {
    node.setAttribute(key, String(value));
  }
  return node;
}

type ObjectState = Readonly<Record<string, unknown>>;

function applyStyle(node: SVGElement, shape: Shape, state: ObjectState): void {
  const fill = resolveReactive(shape.fill, state);
  const opacity = resolveReactive(shape.opacity, state);
  if (fill !== undefined) node.setAttribute("fill", fill);
  if (shape.stroke !== undefined) node.setAttribute("stroke", shape.stroke);
  if (shape.strokeWidth !== undefined) node.setAttribute("stroke-width", String(shape.strokeWidth));
  if (opacity !== undefined) node.setAttribute("opacity", String(opacity));
  if (shape.dash !== undefined) node.setAttribute("stroke-dasharray", shape.dash);
}

/** 火焰外形：一滴水倒过来。size 让不同器材（酒精灯 / 燃烧匙）复用同一个画法。 */
function flamePath(x: number, y: number, size: number): string {
  const h = 22 * size;
  const w = 9 * size;
  return `M${x} ${y - h} C ${x + w} ${y - h * 0.55}, ${x + w} ${y - h * 0.1}, ${x} ${y} C ${x - w} ${y - h * 0.1}, ${x - w} ${y - h * 0.55}, ${x} ${y - h} Z`;
}

/**
 * 由图元建 DOM。返回 null = 本次状态下不该画（`visible` 求值为 false）。
 * 现象的开关就落在这里 —— §9「现象由状态驱动」。
 */
function createShape(shape: Shape, state: ObjectState): SVGElement | null {
  if (resolveReactive(shape.visible, state) === false) return null;

  switch (shape.kind) {
    case "rect":
      return el("rect", { x: shape.x, y: shape.y, width: shape.width, height: shape.height, rx: shape.rx ?? 0 });
    case "ellipse":
      return el("ellipse", { cx: shape.cx, cy: shape.cy, rx: shape.rx, ry: shape.ry });
    case "line":
      return el("line", { x1: shape.x1, y1: shape.y1, x2: shape.x2, y2: shape.y2 });
    case "path":
      return el("path", { d: shape.d });
    case "polygon":
      return el("polygon", { points: shape.points });
    case "text": {
      const node = el("text", { x: shape.x, y: shape.y, "font-size": shape.fontSize ?? 12 });
      node.textContent = resolveReactive(shape.content, state) ?? "";
      return node;
    }
    case "liquid": {
      const level = Math.min(1, Math.max(0, resolveReactive(shape.level, state) ?? 0));
      const height = shape.bounds.height * level;
      return el("rect", {
        x: shape.bounds.x,
        y: shape.bounds.y + shape.bounds.height - height,
        width: shape.bounds.width,
        height,
        rx: 3,
      });
    }
    case "flame": {
      const size = resolveReactive(shape.size, state) ?? 1;
      const node = el("path", { d: flamePath(shape.x, shape.y, size), class: "lab-flame" });
      node.setAttribute("fill", "#f0a032");
      return node;
    }
    case "bubbles": {
      const rate = Math.min(1, Math.max(0, resolveReactive(shape.rate, state) ?? 0));
      if (rate <= 0) return null;
      // 气泡数量随速率增加：气密性检查是零星几个，反应剧烈时连成串
      const count = Math.max(3, Math.round(3 + rate * 9));
      const group = el("g", { class: "lab-bubbles" });
      for (let i = 0; i < count; i += 1) {
        const fraction = (i + 1) / (count + 1);
        const bubble = el("circle", {
          cx: shape.bounds.x + shape.bounds.width * ((i * 0.37) % 1),
          cy: shape.bounds.y + shape.bounds.height * (1 - fraction),
          r: 2 + (i % 3),
          class: "lab-bubble",
        });
        // 每个气泡错开相位，避免整齐划一像在闪
        bubble.style.animationDelay = `${(i * 0.21).toFixed(2)}s`;
        bubble.style.animationDuration = `${(2.2 - rate).toFixed(2)}s`;
        group.append(bubble);
      }
      return group;
    }
  }
}

function poseTransform(object: LabObject): string {
  const { x, y, rotation, scale } = object.pose;
  return `translate(${x} ${y}) rotate(${rotation}) scale(${scale ?? 1})`;
}

/**
 * 「柔性导管」判据：声明了 `tube_path` 端口的器材，其形状由两端锚点决定而不是自身 pose。
 * 用端口 kind 而不是器材类型名判断，是为了不在渲染层写死具体器材（§22 红线 12）。
 */
function isFlexibleConduit(object: LabObject): boolean {
  return (object.ports ?? []).some((port) => port.kind === "tube_path");
}

function conduitPath(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const dx = to.x - from.x;
  const sag = Math.max(24, Math.abs(dx) * 0.35);
  return `M${from.x} ${from.y} C ${from.x + dx * 0.35} ${from.y + sag * 0.2}, ${to.x - dx * 0.2} ${to.y - sag}, ${to.x} ${to.y}`;
}

function renderZones(scene: Scene): SVGGElement {
  const group = el("g", { class: "lab-zones" });
  for (const zone of scene.zones) {
    const rect = el("rect", {
      x: zone.bounds.x,
      y: zone.bounds.y,
      width: zone.bounds.width,
      height: zone.bounds.height,
      rx: 10,
      class: `lab-zone lab-zone--${zone.kind}`,
    });
    rect.dataset.zoneId = zone.id;
    group.append(rect);

    const label = el("text", { x: zone.bounds.x + 12, y: zone.bounds.y + 22, class: "lab-zone-label" });
    label.textContent = zone.name;
    group.append(label);
  }
  return group;
}

function renderPorts(object: LabObject): SVGGElement {
  const group = el("g", { class: "lab-ports" });
  for (const port of object.ports ?? []) {
    const dot = el("circle", { cx: port.anchor.x, cy: port.anchor.y, r: 4, class: "lab-port-dot" });
    dot.dataset.portId = port.id;
    const halo = el("circle", {
      cx: port.anchor.x,
      cy: port.anchor.y,
      r: port.radius ?? 24,
      class: "lab-port-halo",
    });
    // 标签用中文名，且默认不可见 —— 8 件器材 14 个端口同时铺标签会互相重叠，
    // 也违反 §14.2「每一步只突出当前目标」。CSS 控制：hover 或成为吸附候选时才显形。
    const label = el("text", { x: port.anchor.x + 7, y: port.anchor.y - 8, class: "lab-port-label" });
    label.textContent = port.label ?? port.id;
    group.append(halo, dot, label);
  }
  return group;
}

function renderSlots(object: LabObject): SVGGElement {
  const group = el("g", { class: "lab-slots" });
  for (const slot of object.slots ?? []) {
    const box = el("rect", {
      x: slot.bounds.x,
      y: slot.bounds.y,
      width: slot.bounds.width,
      height: slot.bounds.height,
      rx: 4,
      class: "lab-slot-box",
    });
    box.dataset.slotId = slot.id;
    group.append(box);
  }
  return group;
}

/**
 * 连接点的可见性策略（§4.2 `:257` + §22 红线 6「考核模式要能隐藏引导」）：
 *   learn    —— 渲染并常显
 *   practice —— 渲染但默认不可见，靠近（data-snap-target）或 hover 才显形，CSS 控制
 *   exam     —— **完全不渲染**。DOM 里根本没有这些元素，比 CSS 隐藏更强：
 *                考核时学生翻 DOM 也拿不到端口提示。
 */
type AnchorVisibility = "always" | "nearby" | "none";

function anchorVisibilityFor(mode: LabMode): AnchorVisibility {
  if (mode === "learn") return "always";
  if (mode === "practice") return "nearby";
  return "none";
}

function renderObject(object: LabObject, visibility: AnchorVisibility): SVGGElement {
  const group = el("g", { class: "lab-object", transform: poseTransform(object) });
  group.dataset.objectId = object.id;
  group.dataset.objectType = object.type;
  group.dataset.anchors = visibility;
  if (isFlexibleConduit(object)) group.dataset.conduit = "true";

  const body = el("g", { class: "lab-object-body" });
  fillBody(body, object);
  group.append(body);

  const title = document.createElementNS(SVG_NS, "title");
  title.textContent = `${object.name}（${object.id}）`;
  group.append(title);

  if (visibility !== "none") {
    group.append(renderSlots(object), renderPorts(object));
  }
  return group;
}

/** 按当前 state 画出器材本体。state 变了就重画这一组，不动整棵树。 */
function fillBody(body: SVGGElement, object: LabObject): void {
  body.replaceChildren();
  const template = getTemplate(object.type);
  for (const shape of template?.geometry ?? []) {
    const node = createShape(shape, object.state);
    if (!node) continue;
    applyStyle(node, shape, object.state);
    body.append(node);
  }
}

/**
 * 状态签名：只有它变了才重画本体。
 *
 * 直接每帧重画也能对，但拖拽是 60fps 的路径，重画 8 件器材的全部图元会掉帧；
 * 而 `state` 只在规则触发时才变，绝大多数帧签名是不变的。
 */
function stateSignature(object: LabObject): string {
  const template = getTemplate(object.type);
  const reactive = (template?.geometry ?? []).some(
    (shape) =>
      typeof shape.visible === "function" ||
      typeof shape.fill === "function" ||
      typeof shape.opacity === "function" ||
      (shape.kind === "liquid" && typeof shape.level === "function") ||
      (shape.kind === "flame" && typeof shape.size === "function") ||
      (shape.kind === "text" && typeof shape.content === "function"),
  );
  // 没有反应式图元的器材永远不用重画
  return reactive ? JSON.stringify(object.state) : "";
}

export class SvgLayer {
  private root: SVGSVGElement | null = null;
  private host: HTMLElement | null = null;
  private objectLayer: SVGGElement | null = null;
  private relationLayer: SVGGElement | null = null;
  private readonly groups = new Map<string, SVGGElement>();
  private readonly signatures = new Map<string, string>();
  private frame: number | null = null;
  /** 上次 render 用的模式。sync 只在模式没变时生效，切模式必须走 render 重建 DOM。 */
  private mode: LabMode | null = null;
  /** 当前步骤要操作的器材 id。§7.1 `modeDefaults.showGhost` 的落点 */
  private stepTargets: ReadonlySet<string> = new Set();

  /**
   * 告诉渲染层「这一步该动哪几件器材」。
   *
   * 实验包早就用 `modeDefaults.learn.showGhost: true` 声明要这个引导，
   * 但引擎从来没实现 —— 于是学生站在第 3 步不知道该把试剂瓶拖到哪
   * （`bug/023-化学/002-bug.md` 第 1 条）。§14.1 原文要的就是
   * 「对初中生关键步骤给局部视觉线索：端口、目标影子、方向箭头」。
   */
  setStepTargets(objectIds: readonly string[], enabled: boolean): void {
    this.stepTargets = enabled ? new Set(objectIds) : new Set();
    this.syncStepTargets();
  }

  private syncStepTargets(): void {
    for (const [objectId, group] of this.groups) {
      group.dataset.stepTarget = this.stepTargets.has(objectId) ? "true" : "false";
    }
  }

  mount(host: HTMLElement): void {
    this.host = host;
    host.replaceChildren();
    this.root = el("svg", { class: "lab-svg", xmlns: SVG_NS, viewBox: "0 0 960 780" });
    host.append(this.root);
  }

  getRoot(): SVGSVGElement | null {
    return this.root;
  }

  /** 全量渲染。加载实验包、切模式、reset 时调用 —— 不是每帧调用的路径。 */
  render(scene: Scene, objects: LabObject[], mode: LabMode): void {
    const root = this.requireRoot();

    root.setAttribute("viewBox", `0 0 ${scene.stage.width} ${scene.stage.height}`);
    root.dataset.mode = mode;

    this.groups.clear();
    const objectLayer = el("g", { class: "lab-objects" });
    const relationLayer = el("g", { class: "lab-relations" });
    const visibility = anchorVisibilityFor(mode);

    for (const object of [...objects].sort((a, b) => (a.pose.zIndex ?? 0) - (b.pose.zIndex ?? 0))) {
      const group = renderObject(object, visibility);
      this.groups.set(object.id, group);
      objectLayer.append(group);
    }

    this.objectLayer = objectLayer;
    this.relationLayer = relationLayer;
    this.mode = mode;
    this.signatures.clear();
    root.replaceChildren(renderZones(scene), relationLayer, objectLayer);
    this.syncStepTargets();
  }

  /**
   * 增量同步：只更新 transform、层叠序与柔性导管路径。**拖拽每帧走这条路径。**
   * 器材集合本身变了（新增/删除）时要走 render()，这里只处理已有器材的位姿。
   */
  sync(graph: AssemblyGraph, mode: LabMode): void {
    const root = this.root;
    if (!root || !this.objectLayer) return;
    root.dataset.mode = mode;

    // 模式变了要重建 DOM（exam 下连接点必须从 DOM 里消失，不是靠 CSS 藏），
    // 但 sync 拿不到 scene，所以只能由调用方走 render()。这里只保证不误标。
    if (this.mode !== mode) return;

    const objects = Object.values(graph.nodes);

    for (const object of objects) {
      const group = this.groups.get(object.id);
      if (!group) continue;
      if (isFlexibleConduit(object)) {
        this.syncConduit(graph, object, group);
      } else {
        group.setAttribute("transform", poseTransform(object));
      }

      // 状态变了才重画本体（火焰点亮、液面下降、药品出现…）
      const signature = stateSignature(object);
      if (signature !== "" && this.signatures.get(object.id) !== signature) {
        this.signatures.set(object.id, signature);
        const body = group.querySelector(".lab-object-body");
        if (body instanceof SVGGElement) fillBody(body, object);
      }
    }

    this.syncStepTargets();

    // 层叠序：只在需要时重排，避免每帧都动 DOM 顺序
    const ordered = [...objects].sort((a, b) => (a.pose.zIndex ?? 0) - (b.pose.zIndex ?? 0));
    const current = [...this.objectLayer.children];
    const needsReorder = ordered.some(
      (object, index) => current[index] !== this.groups.get(object.id),
    );
    if (needsReorder) {
      for (const object of ordered) {
        const group = this.groups.get(object.id);
        if (group) this.objectLayer.append(group);
      }
    }

    this.syncRelations(graph);
  }

  /** rAF 合帧版 sync：同一帧内多次调用只渲染一次。 */
  scheduleSync(graph: AssemblyGraph, mode: LabMode): void {
    if (this.frame !== null) return;
    const raf =
      typeof globalThis.requestAnimationFrame === "function"
        ? globalThis.requestAnimationFrame.bind(globalThis)
        : (callback: FrameRequestCallback): number => setTimeout(() => callback(0), 16) as unknown as number;

    this.frame = raf(() => {
      this.frame = null;
      this.sync(graph, mode);
    });
  }

  /**
   * 柔性导管：形状由**两端锚点**决定，不跟自身 pose 走 —— 这是 §22 红线 2
   * 「移动试管或集气瓶时导管不能断层、漂移或穿帮」的渲染侧兑现。
   *
   * 整组改用世界坐标（transform 置空），所以端口锚点圆点也要一起搬到世界坐标，
   * 否则连接点会留在旧的局部坐标位置上，视觉上和导管脱节。
   */
  private syncConduit(graph: AssemblyGraph, object: LabObject, group: SVGGElement): void {
    const ends = resolveTubeEnds(graph, object.id);
    if (!ends) return;

    group.setAttribute("transform", "");

    const body = group.querySelector(".lab-object-body");
    if (body instanceof SVGElement) {
      const d = conduitPath(ends.input, ends.output);
      for (const path of body.querySelectorAll("path")) path.setAttribute("d", d);
    }

    const mid = { x: (ends.input.x + ends.output.x) / 2, y: (ends.input.y + ends.output.y) / 2 };
    const worldOf: Record<string, { x: number; y: number }> = {
      input: ends.input,
      output: ends.output,
      path: mid,
    };

    for (const dot of group.querySelectorAll<SVGElement>("[data-port-id]")) {
      const target = worldOf[dot.dataset.portId ?? ""];
      if (!target) continue;
      dot.setAttribute("cx", String(target.x));
      dot.setAttribute("cy", String(target.y));
      const halo = dot.previousElementSibling;
      if (halo instanceof SVGElement && halo.classList.contains("lab-port-halo")) {
        halo.setAttribute("cx", String(target.x));
        halo.setAttribute("cy", String(target.y));
      }
      const label = dot.nextElementSibling;
      if (label instanceof SVGElement && label.classList.contains("lab-port-label")) {
        label.setAttribute("x", String(target.x + 7));
        label.setAttribute("y", String(target.y - 6));
      }
    }
  }

  /**
   * 关系层：**已连接的接点标记** + 拖拽中的预览虚线。
   *
   * 接点标记来自 `bug/023-化学/001-bug.md` 第 1 条「链接成功与否没有鲜明的标志」：
   * 原来连上之后画面和没连上一模一样，只有右侧日志滚一行字。现在每条 active 关系
   * 在接合处画一个实心接点 + 外圈，`data-relation-type` 供 CSS 区分颜色。
   */
  private syncRelations(graph: AssemblyGraph): void {
    const layer = this.relationLayer;
    if (!layer) return;

    layer.replaceChildren();
    const relations = listRelations(graph);

    for (const relation of relations) {
      if (relation.state === "active") {
        const point = this.endpointWorld(graph, relation.to) ?? this.endpointWorld(graph, relation.from);
        if (!point) continue;
        const joint = el("g", { class: "lab-joint" });
        joint.dataset.relationType = relation.type;
        joint.dataset.relationId = relation.id;
        joint.append(
          el("circle", { cx: point.x, cy: point.y, r: 11, class: "lab-joint-ring" }),
          el("circle", { cx: point.x, cy: point.y, r: 5, class: "lab-joint-core" }),
        );
        layer.append(joint);
        continue;
      }

      if (relation.state !== "preview") continue;
      const from = this.endpointWorld(graph, relation.from);
      const to = this.endpointWorld(graph, relation.to);
      if (!from || !to) continue;

      const line = el("line", {
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
        class: "lab-preview-link",
      });
      line.dataset.relationType = relation.type;
      layer.append(line);
    }

    for (const [objectId, group] of this.groups) {
      const involved = relations.some(
        (relation) =>
          relation.state === "preview" &&
          (relation.from.objectId === objectId || relation.to.objectId === objectId),
      );
      group.dataset.snapTarget = involved ? "true" : "false";
    }
  }

  /** 端点 → 世界坐标。预览虚线与接点标记都用它，保证画的位置就是真实锚点。 */
  private endpointWorld(
    graph: AssemblyGraph,
    endpoint: { objectId: string; portId?: string; slotId?: string },
  ): { x: number; y: number } | null {
    const object = graph.nodes[endpoint.objectId];
    if (!object) return null;

    if (endpoint.portId !== undefined) {
      const local = object.ports?.find((port) => port.id === endpoint.portId)?.anchor;
      if (local) {
        // 柔性导管整体用世界坐标绘制，它的端点要走解析出的两端
        if (isFlexibleConduit(object)) {
          const ends = resolveTubeEnds(graph, object.id);
          if (ends && endpoint.portId === "input") return ends.input;
          if (ends && endpoint.portId === "output") return ends.output;
        }
        return toWorld(object, local);
      }
    }
    if (endpoint.slotId !== undefined) {
      const local = object.slots?.find((slot) => slot.id === endpoint.slotId)?.anchor;
      if (local) return toWorld(object, local);
    }
    return { x: object.pose.x, y: object.pose.y };
  }

  /** 新建立的连接闪一下，给一个明确的"成功了"信号（bug/023 第 1 条）。 */
  pulseJoint(relationId: string): void {
    const joint = this.relationLayer?.querySelector(`[data-relation-id="${CSS.escape(relationId)}"]`);
    if (!(joint instanceof SVGElement)) return;
    joint.classList.remove("lab-joint--pulse");
    void joint.getBoundingClientRect();
    joint.classList.add("lab-joint--pulse");
  }

  private requireRoot(): SVGSVGElement {
    if (!this.root) throw new Error("SvgLayer.render 必须在 mount() 之后调用");
    return this.root;
  }

  dispose(): void {
    if (this.frame !== null && typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(this.frame);
    }
    this.frame = null;
    this.groups.clear();
    this.signatures.clear();
    this.objectLayer = null;
    this.relationLayer = null;
    this.mode = null;
    this.host?.replaceChildren();
    this.root = null;
    this.host = null;
  }
}
